import {
  isPayoutResumable,
  resolvePayoutDisplayState,
} from '@/components/payouts/payoutDisplayState';
import { Button, ButtonGroup } from '@chakra-ui/react';
import type { PayoutBatchListItem } from '@fluenceur/types';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface PayoutBatchRowActionsProps {
  payout: PayoutBatchListItem;
  isValidating: boolean;
  isCancelling: boolean;
  onContinue: (payout: PayoutBatchListItem) => void;
  onCancel: (batchId: string) => void;
  onSeeDetails: (batchId: string) => void;
}

/**
 * A batch whose payment was refused stays payable, so it keeps the Continue and
 * Cancel pair: continuing is how the brand retries. The display state decides,
 * so the row can never offer an action the badge contradicts.
 */
const PayoutBatchRowActions: React.FC<PayoutBatchRowActionsProps> = ({
  payout,
  isValidating,
  isCancelling,
  onContinue,
  onCancel,
  onSeeDetails,
}) => {
  const { t } = useTranslation();
  const displayState = resolvePayoutDisplayState(payout);

  if (!isPayoutResumable(displayState)) {
    return (
      <Button
        size="xs"
        variant="outline"
        colorPalette="gray"
        onClick={() => onSeeDetails(payout.id)}
      >
        {t('payouts.table.actions.seeDetails')}
      </Button>
    );
  }

  return (
    <ButtonGroup size="xs" variant="outline">
      <Button
        variant="solid"
        colorPalette="primary"
        loading={isValidating}
        loadingText={t('payouts.table.actions.validating')}
        disabled={isValidating}
        onClick={() => onContinue(payout)}
      >
        {t('payouts.table.actions.continue')}
      </Button>
      <Button
        colorPalette="gray"
        disabled={isValidating || isCancelling}
        onClick={() => onCancel(payout.id)}
      >
        {t('payouts.table.actions.cancel')}
      </Button>
    </ButtonGroup>
  );
};

export default PayoutBatchRowActions;
